import React, { useState } from "react";
import { Button, Modal, __experimentalScrollable as Scrollable, } from "@wordpress/components";
import AttributeEditor from "./AttributeEditor";
import DefaultAttributeEditor from "./DefaultAttributeEditor";
import apiFetch from '@wordpress/api-fetch';
import { dispatch } from '@wordpress/data';
import { store as noticesStore } from '@wordpress/notices';


// EditableTable component for rendering a table with editable fields
const EditableTable = ({ columns, data, updateMyData, onRefresh, modifiedCells }) => {
    const visibleColumns = columns.filter(col => col.isShow);
    const [modal, setModal] = useState({ isOpen: false, data: null, rowIndex: null, columnId: null, fieldType: null, options: [] });

    const isModified = (rowIndex, columnId) => {
        return modifiedCells?.some(cell => cell.rowIndex === rowIndex && cell.columnId === columnId);
    };

    const handleDelete = async (rowIndex, id) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            try {
                const response = await apiFetch({
                    path: `wc/v3/products/${id}`,
                    method: 'DELETE',
                });

                if (response) {
                    // Call the refresh function to update the table data
                    onRefresh();

                }
            } catch (error) {
                console.error('Failed to delete product:', error);
               
            }
        }
    };

    const openModal = (rowIndex, columnId, value = "", fieldType, options = []) => {
        console.log(value);
        setModal({ isOpen: true, data: value, rowIndex, columnId, fieldType, options });
    };

    const saveModalData = () => {
        updateMyData(modal.rowIndex, modal.columnId, modal.data);
        setModal(prev => ({ ...prev, isOpen: false }));
    };

    const handleInputChange = (e, rowIndex, columnId) => {
        const { type, value, checked } = e.target;
        updateMyData(rowIndex, columnId, type === "checkbox" ? checked : value);
    };

    const handleCheckboxChange = (option) => {
        setModal(prev => ({
            ...prev,
            data: prev.data.includes(option) ? prev.data.filter(item => item !== option) : [...prev.data, option]
        }));
    };

    const openMediaUploader = (rowIndex, columnId, value, multiple = false) => {
        const mediaUploader = wp.media({
            title: multiple ? "Select or Upload Images" : "Select or Upload Image",
            button: { text: multiple ? "Use these images" : "Use this image" },
            multiple,
            library: { type: "image" },
        });

        mediaUploader.on("open", () => {
            if (value) {
                const selection = mediaUploader.state().get("selection");
                (Array.isArray(value) ? value : [value]).forEach(id => {
                    const attachment = wp.media.attachment(id);
                    attachment.fetch();
                    selection.add(attachment);
                });
            }
        });

        mediaUploader.on("select", () => {
            const selectedImages = mediaUploader.state().get("selection").map(att => att.toJSON().id);
            updateMyData(rowIndex, columnId, multiple ? selectedImages : selectedImages[0]);
        });

        mediaUploader.open();
    };

    

    return (
        <div className="editable-table-wrapper">
        <div className="editable-table">
            <table >
                <thead>
                    <tr>
                        {visibleColumns.map((column) => (
                            <th key={column.accessor} ckey={column.accessor}>
                                <div>{column.Header}</div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                        {visibleColumns.map((column) => {
                            const cellData = row[column.accessor] || {};
                            const value = cellData.value ?? "";
                            const value_src = cellData.value_src ?? "";
                            const hide = cellData.hide ?? "no";
                            const product_edit_url = row["product_edit_url"]?.value || "#";
                            const product_url = row["product_url"]?.value || "#";

                            // Define shared td style once
                            const tdStyle = {
                            backgroundColor: isModified(rowIndex, column.accessor)
                                ? "#fff3e0"
                                : "transparent",
                            transition: "background-color 0.3s",
                            };

                            // Define content based on input type
                            let content;
                            switch (column.inputType) {
                            case "readonly":
                                content = (
                                <div className="id-cell-container">
                                    <input type="text" readOnly value={value} className="id-input" />
                                    {column.accessor === "id" && (
                                    <div className="action-buttons">
                                        <a
                                        href={product_edit_url}
                                        className="button-icon"
                                        target="_blank"
                                        title="Edit"
                                        >
                                        <span className="dashicons dashicons-edit"></span>
                                        </a>
                                        <a
                                        href={product_url}
                                        className="button-icon"
                                        target="_blank"
                                        title="View"
                                        >
                                        <span className="dashicons dashicons-visibility"></span>
                                        </a>
                                        <a
                                        href="#"
                                        className="button-icon"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDelete(rowIndex, value);
                                        }}
                                        title="Delete"
                                        >
                                        <span className="dashicons dashicons-trash"></span>
                                        </a>
                                    </div>
                                    )}
                                </div>
                                );
                                break;

                            case "text":
                            case "number":
                            case "datetime-local":
                                content =
                                hide === "no" ? (
                                    <input
                                    type={column.inputType}
                                    value={value}
                                    onChange={(e) => handleInputChange(e, rowIndex, column.accessor)}
                                    />
                                ) : (
                                    <span style={{ color: "#666" }}>🚫</span>
                                );
                                break;

                            case "select":
                                content =
                                hide === "no" ? (
                                    <select
                                    value={value}
                                    onChange={(e) => handleInputChange(e, rowIndex, column.accessor)}
                                    >
                                    {column.options.map((option) => (
                                        <option key={option.value} value={option.value}>
                                        {option.label}
                                        </option>
                                    ))}
                                    </select>
                                ) : (
                                    <span style={{ color: "#666" }}>🚫</span>
                                );
                                break;

                            case "checkbox":
                                content =
                                hide === "no" ? (
                                    <input
                                    type="checkbox"
                                    checked={Boolean(value)}
                                    onChange={(e) => handleInputChange(e, rowIndex, column.accessor)}
                                    />
                                ) : (
                                    <span style={{ color: "#666" }}>🚫</span>
                                );
                                break;

                            case "multipleCheckbox":
                                content =
                                hide === "no" ? (
                                    <Button
                                    variant="secondary"
                                    onClick={() =>
                                        openModal(rowIndex, column.accessor, value, "multipleCheckbox", column.options)
                                    }
                                    >
                                    Edit
                                    </Button>
                                ) : null;
                                break;

                            case "editor":
                                content =
                                hide === "no" ? (
                                    <Button
                                    variant="secondary"
                                    onClick={() =>
                                        openModal(rowIndex, column.accessor, value, "editor")
                                    }
                                    >
                                    Edit
                                    </Button>
                                ) : null;
                                break;

                            case "image":
                                    content = (
                                        <div
                                            onClick={() => openMediaUploader(rowIndex, column.accessor, value, false)}
                                             style={{
                                                         cursor: "pointer",
                                                    }}
                                            
                                        >
                                            {value_src ? (
                                                <img
                                                    src={value_src}
                                                    alt="Preview"
                                                    style={{
                                                         width: "30px",
                                                         height: "30px",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                            ) : (
                                                <span style={{ color: "#999", fontSize: "12px" }}>Upload</span>
                                            )}
                                        </div>
                                    );
                                    break;


                            case "gallery":
                                content = (
                                        <div
                                            onClick={() => openMediaUploader(rowIndex, column.accessor, value, true)}
                                            style={{
                                                         cursor: "pointer",
                                                    }}
                                        >
                                            {value_src ? (
                                                <img
                                                    src={value_src}
                                                    alt="Preview"
                                                    style={{
                                                         width: "30px",
                                                height: "30px",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                            ) : (
                                                <span style={{ color: "#999", fontSize: "12px" }}>Upload</span>
                                            )}
                                        </div>
                                    );
                                break;

                            case "attribute":
                                content = (
                                <Button
                                    variant="secondary"
                                    onClick={() => openModal(rowIndex, column.accessor, value, "attribute")}
                                >
                                    Edit Attribute
                                </Button>
                                );
                                break;

                            case "default_attribute":
                                content =
                                hide === "no" ? (
                                    <Button
                                    variant="secondary"
                                    onClick={() =>
                                        openModal(rowIndex, column.accessor, value, "default_attribute")
                                    }
                                    >
                                    Edit Default Attribute
                                    </Button>
                                ) : null;
                                break;

                            default:
                                content = value;
                                break;
                            }

                            return (
                            <td key={column.accessor} style={tdStyle}>
                                {content}
                            </td>
                            );
                        })}
                        </tr>
                    ))}
                    </tbody>

            </table>

            {/* Modal for editing */}
                {modal.isOpen && (
                   <Modal title="Edit Content" onRequestClose={() => setModal(prev => ({ ...prev, isOpen: false }))}>
                        <div className="modal-content">
                            {modal.fieldType === "editor" && (
                                <>
                                    <textarea
                                        value={modal.data}
                                        onChange={e => setModal(prev => ({ ...prev, data: e.target.value }))}
                                        style={{ width: "100%", height: "150px" }}
                                    />
                                </>
                            )}
                           {modal.fieldType === "attribute" && (
                                <AttributeEditor
                                    modal={modal}
                                    setModal={setModal}
                                />
                            )}
                            {modal.fieldType === "default_attribute" && (
                                <DefaultAttributeEditor
                                    modal={modal}
                                    setModal={setModal}
                                />
                            )}

                                   







                            {modal.fieldType === "multipleCheckbox" && (
                                <>
                                    <Scrollable
                                            scrollDirection="y"
                                            style={{ height: 300 }}
                                        >
                                             {modal.options.map(opt => (
                                                <div key={opt.value} style={{ marginBottom: "5px" }}>
                                                    <input type="checkbox" 
                                                    value={opt.value} 
                                                    checked={modal.data.includes(opt.value)} 
                                                    onChange={() => handleCheckboxChange(opt.value)} />
                                                    {opt.label}
                                                </div>
                                            ))}
                                    </Scrollable>
                                </>
                            )}

                             <div className="modal-actions" style={{ marginTop: "10px" }}>
                                <Button variant="primary" onClick={saveModalData}>Save</Button>
                                <Button variant="secondary" onClick={() => setModal(prev => ({ ...prev, isOpen: false }))}>Cancel</Button>
                            </div>
                        </div>
                    </Modal>
                )}
        </div>
    </div>
    );
};

export default EditableTable;
